import moment from 'moment';

import { formatNumberWithMonedaSigno } from '../../../assets/shared/formatNumber';

const textoHora = (nodo) => (nodo.hora || '').trim();

/** Del más viejo al más nuevo: Vnt_Cabecera.Fecha (día) + Hora ("HH:MM:SS.f", se compara como texto). */
const porFecha = (a, b) =>
  moment(a.fecha).startOf('day').valueOf() - moment(b.fecha).startOf('day').valueOf() ||
  (textoHora(a) < textoHora(b) ? -1 : textoHora(a) > textoHora(b) ? 1 : 0) ||
  a.nroReferencia - b.nroReferencia;

/**
 * Arma el circuito que devuelve la API para recorrerlo: raiz es el comprobante consultado (que la API no
 * incluye en nodos). Entre cada origen y destino se juntan los artículos descargados o, si el destino es un
 * recibo, lo que le imputó a la factura.
 */
const armarCircuito = (raiz, circuito) => {
  const nodoRaiz = { ...raiz, clave: circuito.clave, nivel: 0, anulado: raiz.estado === 'Anulado' || !!raiz.anulado };
  const nodos = { [nodoRaiz.clave]: nodoRaiz };
  circuito.nodos.forEach((nodo) => {
    nodos[nodo.clave] = nodo;
  });

  const enlaces = {};
  circuito.relaciones.forEach((relacion) => {
    const clave = `${relacion.claveOrigen}>${relacion.claveDestino}`;
    const enlace = enlaces[clave] || (enlaces[clave] = { origen: relacion.claveOrigen, destino: relacion.claveDestino, articulos: {}, importe: null });
    if (relacion.importe !== null && relacion.importe !== undefined) {
      enlace.importe = (enlace.importe || 0) + relacion.importe;
    } else {
      enlace.articulos[relacion.idArticulo] = (enlace.articulos[relacion.idArticulo] || 0) + relacion.cantidad;
    }
  });
  const listaEnlaces = Object.values(enlaces).filter((enlace) => nodos[enlace.origen] && nodos[enlace.destino]);

  const relacionados = (desde, hacia) => (clave) =>
    listaEnlaces
      .filter((enlace) => enlace[desde] === clave)
      .map((enlace) => ({ nodo: nodos[enlace[hacia]], enlace }))
      .sort((a, b) => porFecha(a.nodo, b.nodo));

  const todos = Object.values(nodos).sort(porFecha);
  const niveles = [];
  todos.forEach((nodo) => {
    (niveles[nodo.nivel] = niveles[nodo.nivel] || []).push(nodo);
  });

  return {
    raiz: nodoRaiz,
    /** Todos los comprobantes, la raíz incluida, del más viejo al más nuevo. */
    todos,
    /** Comprobantes por nivel (0 = raíz). */
    niveles: niveles.filter(Boolean),
    /** Los que descargaron al comprobante: [{ nodo, enlace }]. */
    hijos: relacionados('origen', 'destino'),
    /** Los comprobantes del circuito que descargó el comprobante: [{ nodo, enlace }]. */
    origenes: relacionados('destino', 'origen')
  };
};

/** El enlace es una imputación (factura → recibo) y no una descarga. */
export const esImputacion = (enlace) => enlace.importe !== null;

/** Qué hizo el comprobante con su origen: un recibo "paga" la factura, el resto la "descarga". */
export const verboEnlace = (enlace) => (esImputacion(enlace) ? 'paga' : 'descarga');

/** "Descargó 3 artículos · 25 u." o, si es una imputación, "Imputó 1.234,56". */
export const resumenEnlace = (enlace) => {
  if (esImputacion(enlace)) return `Imputó ${formatNumberWithMonedaSigno(enlace.importe)}`;
  const articulos = Object.keys(enlace.articulos);
  const unidades = articulos.reduce((total, articulo) => total + enlace.articulos[articulo], 0);
  return `Descargó ${articulos.length} ${articulos.length === 1 ? 'artículo' : 'artículos'} · ${unidades} u.`;
};

/** Detalle por artículo de un enlace, para el tooltip. */
export const detalleArticulos = (enlace) =>
  Object.keys(enlace.articulos)
    .map((articulo) => `${articulo}: ${enlace.articulos[articulo]} u.`)
    .join('\n');

export default armarCircuito;
